'use client';

import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, AlertCircle, Info, X } from 'lucide-react';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface NotificationData {
    id: string;
    type: NotificationType;
    message: string;
    description?: string;
    duration?: number;
}

interface NotificationProps {
    notification: NotificationData;
    onDismiss: (id: string) => void;
}

const notificationConfig = {
    success: {
        icon: CheckCircle2,
        iconColor: 'text-emerald-500',
        accentColor: 'bg-emerald-500',
    },
    error: {
        icon: XCircle,
        iconColor: 'text-red-500',
        accentColor: 'bg-red-500',
    },
    warning: {
        icon: AlertCircle,
        iconColor: 'text-amber-500',
        accentColor: 'bg-amber-500',
    },
    info: {
        icon: Info,
        iconColor: 'text-blue-500',
        accentColor: 'bg-blue-500',
    },
};

export function Notification({ notification, onDismiss }: NotificationProps) {
    const config = notificationConfig[notification.type];
    const Icon = config.icon;
    const duration = notification.duration ?? 1800;

    useEffect(() => {
        const timer = setTimeout(() => onDismiss(notification.id), duration);
        return () => clearTimeout(timer);
    }, [notification.id, duration, onDismiss]);

    return (
        <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="flex items-start gap-3 bg-white border border-gray-200 rounded-2xl shadow-md px-4 py-3.5 min-w-[300px] max-w-[380px]"
        >
            {/* Left accent bar */}
            <div className={`absolute left-0 top-3 bottom-3 w-[3px] rounded-full ${config.accentColor}`} />

            <Icon className={`size-[18px] ${config.iconColor} shrink-0 mt-0.5`} />

            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 leading-snug">
                    {notification.message}
                </p>
                {notification.description && (
                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                        {notification.description}
                    </p>
                )}
            </div>

            <button
                onClick={() => onDismiss(notification.id)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors shrink-0 -mr-1"
                aria-label="Dismiss"
            >
                <X className="size-3.5 text-gray-400" />
            </button>
        </motion.div>
    );
}

interface NotificationContainerProps {
    notifications: NotificationData[];
    onDismiss: (id: string) => void;
}

export function NotificationContainer({ notifications, onDismiss }: NotificationContainerProps) {
    return (
        <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
            <AnimatePresence mode="popLayout">
                {notifications.map((notification) => (
                    <div key={notification.id} className="pointer-events-auto relative">
                        <Notification notification={notification} onDismiss={onDismiss} />
                    </div>
                ))}
            </AnimatePresence>
        </div>
    );
}
